import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useRejectDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealOpportunityLimitedPartnerViewV3QueryKey } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import {
  trackCoInvestDealRejectAttempted,
  trackCoInvestRejectModalOpened,
} from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { useOrganization } from "@vega-modules/hooks/useOrganization";

import { type DeclineStage } from "../components/RejectDealModal";

type UseRejectDealProps = {
  dealOpportunityId: string;
  dealId?: string;
  declineStage: DeclineStage;
};

export const useRejectDeal = ({
  dealOpportunityId,
  dealId,
  declineStage,
}: UseRejectDealProps) => {
  const { trackEvent } = useAnalytics();
  const { selectedOrgId: limitedPartnerId } = useOrganization();
  const showErrorToast = useCoInvestErrorToast();
  const showToast = useToast();
  const queryClient = useQueryClient();
  const rejectModal = useBoolean(false);

  const { mutate: rejectDealOpportunity, isPending: isRejectingDeal } =
    useRejectDealOpportunity({
      mutation: {
        onSuccess: () => {
          // Track deal rejection
          trackEvent(AnalyticsEvent.COINVEST_DEAL_REJECTED, undefined, {
            dealId,
            dealOpportunityId,
            limitedPartnerId,
          });

          showToast({
            variant: "success",
            title: m.status_success(),
            description:
              declineStage === "post-allocation"
                ? m.coInvestments_declineAllocation_successMessage()
                : m.coInvestments_declineDeal_successMessage(),
          });

          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityLimitedPartnerViewV3QueryKey(
                dealOpportunityId,
              ),
          });

          rejectModal.setFalse();
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_declineDeal_errorMessage(),
          });
        },
      },
    });

  const handleRejectDeal = useCallback(
    ({ reason, comment }: { reason: string; comment?: string }) => {
      trackCoInvestDealRejectAttempted({ dealOpportunityId });
      rejectDealOpportunity({
        dealOpportunityId,
        data: { reason, comment },
      });
    },
    [dealOpportunityId, rejectDealOpportunity],
  );

  const handleRejectDealClick = useCallback(() => {
    trackCoInvestRejectModalOpened({ dealOpportunityId });
    rejectModal.setTrue();
  }, [dealOpportunityId, rejectModal]);

  const handleRejectModalClose = useCallback(() => {
    if (!isRejectingDeal) {
      rejectModal.setFalse();
    }
  }, [isRejectingDeal, rejectModal]);

  return {
    handleRejectDeal,
    isRejectingDeal,
    isLoading: isRejectingDeal,
    rejectModal: {
      isOpen: rejectModal.isOpen,
      onOpen: handleRejectDealClick,
      onClose: handleRejectModalClose,
    },
  };
};
